/**
 * MONSTER TOWN — THE THINGS IN THE WORLD. FISHING/BUGS CREW (src/catching/**).
 *
 * Three tiny bits of procedural art, all built from `src/render/kit.ts` so a
 * bug built two hundred times does not create two hundred materials (the
 * older-iPad rule that the README calls out by name):
 *
 *   creatureMesh()  a bug you can see from across the town and tap
 *   floatMesh()     the fishing float, and its ripple ring
 *   catchMesh()     the fish that leaps out of the water when you get it
 *
 * SILHOUETTE FIRST. These are 20–30 cm objects in a 48x48 metre town, seen from
 * a fixed isometric camera at four zoom steps. Detail is invisible; SHAPE and
 * MOTION are not. So a butterfly is two big flat wings that flap, a firefly is a
 * glowing bead — and every one of them carries a "here I am" plate so it reads
 * against grass at the widest zoom. (Without it the round-one bugs were
 * genuinely hard to find, which for a three-year-old is the same as not being
 * there.)
 *
 * ===========================================================================
 *  A CRAWLER WEARS ITS OWN BOOK PICTURE. Read this before touching `creatureMesh`.
 * ===========================================================================
 * Round `vr1`: "the ladybug in the world is an unreadable red lozenge; the book
 * promises a beautiful beetle." It was true and it was backwards — in a
 * find-them-and-fill-the-book loop the DISCOVERY has to be the good art and the
 * reward screen can afford to be a repeat. The crawler was a flattened ball with
 * a bead for a head: about 20 screen pixels of featureless red-and-black at the
 * default zoom, no spots, no dome, no split, no antennae.
 *
 * It is now the SAME PICTURE the book prints — `CollectionEntry.draw`, baked
 * once into a cached 128 px texture and laid face-up on a low dome. Nothing is
 * drawn twice and nothing can drift: change the ladybird's spots and both the
 * grass and the page change together, because there is only one ladybird.
 * A creature opts in with one data field, `CreatureLook.topDown` — true only
 * where the book picture really is a plan view (a ladybird is, a snail is not).
 *
 * NO SOFT EDGES. The old plate was a radial gradient, and at bug size it read as
 * a smudge on the lens in a game where every other edge is a hard ink line. Both
 * plates below are STEPPED — flat bands with crisp boundaries — which is the
 * same graphic vocabulary as the fishing ripple and the lamp pools.
 *
 * NO OUTLINES. `inked()` doubles a mesh's draw calls and these are the smallest
 * objects on screen; the plate does the separating instead, for one extra quad —
 * and a crawler's ink line comes free, drawn into its own picture.
 */

import * as THREE from 'three'
import { css, hex, type PaletteName } from '../core/palette'
import { clock } from '../core/time'
import {
  ball,
  boxGeo,
  flat,
  generatedTexture,
  group,
  planeGeo,
  quadGeo,
  softDisc,
  sphereGeo,
  texturedFlat,
  toon,
} from '../render/kit'
import {
  CREATURE_LOOK,
  creaturePainter,
  type CreatureLook,
  type CreatureMotion,
} from '../content/creatures'

/** Fallback so a creature with no look row still gets a body, never nothing. */
const DEFAULT_LOOK: CreatureLook = { body: 'monsterLime', accent: 'grassDark', motion: 'flutter' }

export function lookOf(id: string): CreatureLook {
  return CREATURE_LOOK[id] ?? DEFAULT_LOOK
}

// ---------------------------------------------------------------------------
// the "here I am" plates — cached texture + cached geometry, shared by everything
// ---------------------------------------------------------------------------

/**
 * Paint a stack of flat bands with HARD boundaries. `bands` is read outwards
 * from the middle as [radius 0..1, alpha]; anything past the last band is
 * nothing at all. No gradient anywhere — that is the whole point.
 */
function stepped(bands: readonly (readonly [number, number])[]) {
  return (c: CanvasRenderingContext2D, s: number): void => {
    const r = s / 2
    c.clearRect(0, 0, s, s)
    // Outermost first so each inner band paints cleanly over the one before.
    for (let i = bands.length - 1; i >= 0; i--) {
      const band = bands[i]
      if (band === undefined) continue
      c.beginPath()
      c.arc(r, r, r * band[0], 0, Math.PI * 2)
      c.fillStyle = `rgba(255,255,255,${band[1]})`
      c.fill()
    }
  }
}

/** The upright plate behind a flyer, the float and the leaping fish. */
const GLOW_BANDS = [
  [0.42, 0.62],
  [0.62, 0.34],
  [0.82, 0.16],
] as const

/** The flat plate a crawler sits on, so a kid can spot one in long grass. */
const PUCK_BANDS = [
  [0.5, 0.0],
  [0.72, 0.34],
  [0.86, 0.14],
] as const

function glowMat(): THREE.Material {
  return texturedFlat('catch:glow', stepped(GLOW_BANDS))
}

/** Upright, faces the camera's default quarter. Kept for flyers and the water. */
function halo(size: number): THREE.Mesh {
  const m = new THREE.Mesh(quadGeo(size, size), glowMat())
  m.name = 'halo'
  m.renderOrder = -1
  m.castShadow = false
  m.receiveShadow = false
  return m
}

/**
 * The ground plate. Lies flat at `y`, hard-edged, and it is a RING — the middle
 * is empty, so it frames the creature instead of washing it out.
 */
function puck(size: number, y: number): THREE.Mesh {
  const m = new THREE.Mesh(planeGeo(size, size), texturedFlat('catch:puck', stepped(PUCK_BANDS)))
  m.name = 'puck'
  m.position.y = y
  m.renderOrder = -1
  m.castShadow = false
  m.receiveShadow = false
  return m
}

/**
 * The creature's OWN BOOK PICTURE as a flat, face-up, unlit quad. One cached
 * 128 px texture and one cached material per creature, so a hundred ladybirds
 * cost one of each — the older-iPad rule this file opens with.
 */
function bookFace(id: string, size: number): THREE.Mesh | null {
  const painter = creaturePainter(id)
  if (painter === null) return null
  const mat = texturedFlat(`catch:face:${id}`, (c, s) => {
    painter(c, s)
  })
  const m = new THREE.Mesh(planeGeo(size, size), mat)
  // `planeGeo` puts the top of the canvas at -Z; the creature's front is +Z.
  m.rotation.y = Math.PI
  m.name = 'face'
  m.castShadow = false
  m.receiveShadow = false
  return m
}

/** Kept so a tool can prove the plates really are code-generated textures. */
function platesReady(): boolean {
  return generatedTexture('catch:glow', stepped(GLOW_BANDS)) !== undefined
}

// ---------------------------------------------------------------------------
// creatures
// ---------------------------------------------------------------------------

/**
 * A WING IS A WING-SHAPE, NOT A RECTANGLE.
 *
 * Round `vr2`, on the night creature: "a faint white-grey shape on dark green
 * lawn". Half of that is the light (see `lantern` below) and half of it is this
 * — the wings were bare `quadGeo` rectangles in a flat colour, so a moth from
 * above was two pale oblongs and a brown box. Rectangles are the one shape
 * nothing alive has.
 *
 * The quad now carries a cached, code-generated WING silhouette instead: a
 * rounded blade with a darker rim and two pale eye-spots, drawn white and
 * TINTED by the material, so every flyer in the game shares one 128 px texture
 * and one material per colour. Alpha does the shaping, which means the
 * silhouette is a wing at any zoom and there is still exactly one quad per wing.
 * It is deliberately symmetric left-to-right so both sides use the same texture
 * and neither needs a mirrored copy.
 */
function wingDraw(c: CanvasRenderingContext2D, s: number): void {
  c.clearRect(0, 0, s, s)
  const k = s / 100
  c.save()
  c.scale(k, k)
  // the blade: a rounded lozenge, fat at the middle, tapered at both ends
  c.beginPath()
  c.moveTo(4, 50)
  c.bezierCurveTo(12, 12, 88, 12, 96, 50)
  c.bezierCurveTo(88, 88, 12, 88, 4, 50)
  c.closePath()
  c.fillStyle = 'rgba(255,255,255,1)'
  c.fill()
  // a darker rim, which the material's tint turns into a shade of the wing
  c.lineWidth = 9
  c.strokeStyle = 'rgba(150,150,150,1)'
  c.stroke()
  // two eye-spots, the marking every wing in this game's book has
  for (const x of [34, 66]) {
    c.beginPath()
    c.arc(x, 50, 11, 0, Math.PI * 2)
    c.fillStyle = 'rgba(178,178,178,1)'
    c.fill()
  }
  c.restore()
}

/**
 * ONE WING, ON A PIVOT AT THE BODY.
 *
 * READ THE PICTURE BEFORE CHANGING THIS. The first version hung two upright
 * quads either side of the body, which is what a butterfly looks like from the
 * SIDE — and this game has a fixed isometric camera looking down at about 35
 * degrees, so every moth in the captured night frame read as a folded scrap of
 * white paper lying on the lawn. The wings now lie FLAT (the quad is rotated
 * face-up inside the pivot) and the flap raises them about the body's long
 * axis, so the silhouette from above is a wing shape either side of a dark
 * body: unmistakably an insect, at any zoom.
 */
function wing(w: number, h: number, colour: PaletteName, side: number): THREE.Group {
  const pivot = group(side < 0 ? 'wingL' : 'wingR')
  const m = new THREE.Mesh(quadGeo(w, h), texturedFlat('catch:wing', wingDraw, {
    color: colour,
    opacity: 0.97,
  }))
  m.rotation.x = -Math.PI / 2
  m.position.set((side * w) / 2, 0, 0)
  m.castShadow = false
  m.receiveShadow = false
  pivot.add(m)
  return pivot
}

/**
 * ===========================================================================
 *  A NIGHT CREATURE CARRIES ITS OWN LIGHT — AND IT HAS TO ESCAPE THE NIGHT WASH
 * ===========================================================================
 * Round `vr2`: "the spawned night creature is a faint white-grey shape on dark
 * green lawn with only a thin halo ring; at plaza distance it is almost
 * invisible, which undercuts the point of night creatures being worth going out
 * for."
 *
 * The cause is documented in the README under Time of day, one crew over: the
 * whole picture is graded after dark by a MULTIPLY pane at `renderOrder` 9990.
 * A creature's art — including its unlit "here I am" plate — is drawn long
 * before that, so it is multiplied down along with the grass, and a pale lilac
 * moth on dark grass loses exactly the contrast it was designed to have. The
 * moth was never the wrong colour; it was under the same blanket as everything
 * else.
 *
 * The time crew's own answer to the same problem is the lamp pools, which sit
 * at 9995 — deliberately BETWEEN the multiply and the warm lift, because a pool
 * of light is a light and the night must not multiply it down. A firefly's
 * lantern is the same claim, so it takes the same band. The material is built
 * here rather than through `texturedFlat` for one reason: it is ADDITIVE, and
 * additive is the only blend that reads as "something is glowing" rather than
 * "something pale is painted on top" (see `tint.ts` on why a normal-blended
 * pane read as concrete).
 *
 * It is opt-in per creature, by ONE DATA FIELD (`CreatureLook.lantern`), so a
 * new night creature is still a data drop and nothing here needs a species name.
 */
/**
 * Two materials per colour, and the split matters: `pool` is the disc on the
 * ground and `bloom` is the halo AROUND the creature. The bloom is drawn after
 * the creature (it has to be, to clear the night multiply) so an additive halo
 * at pool strength paints the moth's own wings out — measured, in
 * `shots/fix-catch-r2/16b`: a white blob with a dark speck in it. It burns at
 * `BLOOM_SHARE` of the pool.
 */
const BLOOM_SHARE = 0.22
const lanternMats: THREE.MeshBasicMaterial[] = []
const lanternPools = new Map<string, THREE.MeshBasicMaterial>()

function lanternMat(colour: PaletteName, role: 'pool' | 'bloom'): THREE.MeshBasicMaterial {
  const key = `${colour}:${role}`
  const cached = lanternPools.get(key)
  if (cached !== undefined) return cached
  const m = new THREE.MeshBasicMaterial({
    map: generatedTexture('catch:lantern', softDisc(0.18)),
    color: hex(colour),
    transparent: true,
    opacity: role === 'pool' ? 0.5 : 0.5 * BLOOM_SHARE,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
  m.userData['role'] = role
  lanternPools.set(key, m)
  lanternMats.push(m)
  return m
}

/**
 * A POOL ON THE GRASS, AND A BLOOM AROUND THE CREATURE — and the pool is the
 * half that does the work.
 *
 * The first version was one upright quad, and it was invisible in the captured
 * frame for a reason worth writing down: `bugs.ts` spins a creature's whole
 * group (`art.rotation.y = -ang`) as it pootles round its patch, so an upright
 * billboard-less quad goes EDGE ON to a fixed isometric camera twice a circuit
 * and is a hairline for a good part of every lap. A flat disc on the ground
 * looks the same at every yaw — which is exactly why the time crew's lamp pools
 * are discs — so the pool is the reliable half, and the upright bloom is
 * counter-rotated in `animateCreature` so it never turns edge on either.
 *
 * `hoverY` is how high the caller is about to hang this creature; the pool has
 * to lie on the GRASS, not travel up with a flyer.
 */
function lantern(colour: PaletteName, size: number, hoverY: number, scale: number): THREE.Group {
  const g = group('lantern')
  // 9995: above the night MULTIPLY (9990), below the warm ADDITIVE lift (9998).
  // The same band, and for the same reason, as the time crew's lamp pools.
  const pool = new THREE.Mesh(planeGeo(size, size), lanternMat(colour, 'pool'))
  pool.position.y = (0.06 - hoverY) / scale
  pool.renderOrder = 9995
  pool.castShadow = false
  pool.receiveShadow = false
  g.add(pool)
  const bloom = new THREE.Mesh(quadGeo(size * 0.32, size * 0.32), lanternMat(colour, 'bloom'))
  bloom.renderOrder = 9995
  bloom.castShadow = false
  bloom.receiveShadow = false
  g.add(bloom)
  return g
}

/**
 * How strongly every lantern in the town is burning. ONE number, read off the
 * clock, written to the SHARED material — which is correct here and would be a
 * bug anywhere else: darkness is global, so two moths on opposite sides of the
 * town really are lit the same amount. A lantern is nearly out at noon and full
 * after dark, so a creature that glows does not glow in broad daylight.
 */
function burnLanterns(darkness: number): void {
  const k = 0.1 + 0.72 * Math.max(0, Math.min(1, darkness))
  for (const m of lanternMats) {
    m.opacity = m.userData['role'] === 'bloom' ? k * BLOOM_SHARE : k
  }
}

/**
 * One bug, origin at its own centre. Named parts the animator moves:
 *   'wingL' / 'wingR'  flap
 *   'body'             bob and lean
 *   'glow'             the firefly's lantern (absent on everything else)
 *
 * `hoverY` is how high above the ground the caller is about to hang this group.
 * A crawler's plate has to lie on the GRASS, not travel with the creature, and
 * only the caller knows where the grass is from here.
 */
export function creatureMesh(id: string, hoverY = 0): THREE.Group {
  const look = lookOf(id)
  const g = group(`bug:${id}`)
  /** Everything below is authored pre-scale; `g` is scaled once at the end. */
  const S = 1.35

  const body = group('body')
  g.add(body)

  // The lantern goes on FIRST so it is behind everything the creature is made
  // of; being additive and at 9995 it lifts the art out of the night wash
  // instead of painting over it. See `lantern` above.
  if (look.lantern !== undefined) g.add(lantern(look.lantern, 4.4 / S, hoverY, S))

  const motion: CreatureMotion = look.motion
  const face = motion === 'crawl' && look.topDown === true ? bookFace(id, 1.15 / S) : null
  if (face !== null) {
    /**
     * THE PICTURE IS THE CREATURE. A low dome underneath gives it thickness at
     * the silhouette, the face gives it spots, a split, eyes and antennae — and
     * because the face IS `CollectionEntry.draw`, the ladybird in the grass and
     * the ladybird on the page cannot disagree.
     */
    g.add(puck(1.5, (0.03 - hoverY) / S))
    const dome = ball(0.3, { color: look.body, seg: 10 })
    dome.scale.set(1, 0.44, 1)
    dome.castShadow = false
    dome.receiveShadow = false
    body.add(dome)
    face.position.y = 0.14
    body.add(face)
    g.scale.setScalar(S)
    return g
  }

  g.add(motion === 'crawl' || motion === 'hop' ? puck(1.5, (0.03 - hoverY) / S) : halo(1.5))
  if (motion === 'flutter') {
    body.add(wing(0.52, 0.44, look.body, -1), wing(0.52, 0.44, look.body, 1))
    const core = new THREE.Mesh(boxGeo(0.13, 0.13, 0.4), toon(look.accent, { flat: true }))
    core.castShadow = false
    body.add(core)
    // A head, so it has a front. At this size that is the difference between
    // "an insect" and "a shape".
    const head = ball(0.09, { color: look.accent, seg: 7 })
    head.position.set(0, 0.02, 0.22)
    head.castShadow = false
    body.add(head)
  } else if (motion === 'glow') {
    const bead = ball(0.15, { color: look.accent, seg: 8 })
    bead.castShadow = false
    body.add(bead)
    const glow = new THREE.Mesh(sphereGeo(0.28, 8), flat(look.body, 0.5))
    glow.name = 'glow'
    glow.castShadow = false
    body.add(glow)
    body.add(wing(0.34, 0.26, 'glass', -1), wing(0.34, 0.26, 'glass', 1))
  } else {
    // crawl + hop: a chunky shell, which is the whole silhouette at this size
    const shell = ball(0.2, { color: look.body, seg: 9 })
    shell.scale.set(1, 0.72, 1.25)
    shell.castShadow = false
    body.add(shell)
    const head = ball(0.11, { color: look.accent, seg: 8 })
    head.position.set(0, 0.02, 0.22)
    head.castShadow = false
    body.add(head)
    if (motion === 'hop') {
      const leg = new THREE.Mesh(boxGeo(0.06, 0.22, 0.06), toon(look.accent, { flat: true }))
      leg.position.set(0.16, -0.04, -0.14)
      leg.rotation.z = -0.5
      leg.castShadow = false
      body.add(leg)
      const leg2 = leg.clone()
      leg2.position.x = -0.16
      leg2.rotation.z = 0.5
      body.add(leg2)
    }
  }
  g.scale.setScalar(1.35)
  return g
}

/**
 * The moving parts of one bug, looked up ONCE at spawn.
 *
 * `getObjectByName` walks the subtree, and this runs on every awake frame for
 * every bug on screen; on the hand-me-down iPad the README is explicit that
 * there is almost no p50 headroom left, so a per-frame name search is exactly
 * the kind of small cost this crew does not get to spend.
 */
export interface CreatureParts {
  body: THREE.Object3D | null
  wingL: THREE.Object3D | null
  wingR: THREE.Object3D | null
  glow: THREE.Object3D | null
  lantern: THREE.Object3D | null
}

export function creatureParts(g: THREE.Group): CreatureParts {
  return {
    body: g.getObjectByName('body') ?? null,
    wingL: g.getObjectByName('wingL') ?? null,
    wingR: g.getObjectByName('wingR') ?? null,
    glow: g.getObjectByName('glow') ?? null,
    lantern: g.getObjectByName('lantern') ?? null,
  }
}

/** Animate one bug. `t` is seconds of life; cheap trig only, no allocation. */
export function animateCreature(parts: CreatureParts, id: string, t: number): void {
  const look = lookOf(id)
  const body = parts.body
  if (body === null) return
  const wl = parts.wingL
  const wr = parts.wingR
  if (look.motion === 'flutter' || look.motion === 'glow') {
    // 0 = flat and fully readable from above, 1 = raised. It never folds all
    // the way shut, so there is no frame in which the bug disappears.
    const flap = 0.15 + 0.55 * (0.5 + 0.5 * Math.sin(t * 12))
    if (wl !== null) wl.rotation.z = flap
    if (wr !== null) wr.rotation.z = -flap
    body.position.y = Math.sin(t * 3.1) * 0.09
    body.rotation.y = Math.sin(t * 0.9) * 0.4
  } else if (look.motion === 'hop') {
    const k = t % 1.6
    body.position.y = k < 0.35 ? Math.sin((k / 0.35) * Math.PI) * 0.35 : 0
    body.rotation.z = Math.sin(t * 2) * 0.08
  } else {
    body.position.y = Math.sin(t * 2.2) * 0.03
    body.rotation.z = Math.sin(t * 1.6) * 0.1
  }
  const glow = parts.glow
  if (glow !== null) {
    // The firefly PULSES. It is the whole reason a kid comes back at dusk.
    const k = 0.55 + 0.45 * Math.sin(t * 2.6)
    glow.scale.setScalar(0.8 + k * 0.6)
    const m = (glow as THREE.Mesh).material
    if (!Array.isArray(m) && 'opacity' in m) (m as THREE.Material & { opacity: number }).opacity = 0.25 + k * 0.4
  }
  const lamp = parts.lantern
  if (lamp !== null) {
    /**
     * The lantern BREATHES, and it never goes out. Two things happen here and
     * neither is `opacity` on the mesh: the material is shared by every creature
     * wearing the same colour, so per-creature brightness would make two moths
     * on opposite sides of the town pulse in lockstep. Brightness is global and
     * belongs to `burnLanterns`; what is per-creature is the SCALE, and the
     * counter-rotation — `bugs.ts` spins the parent, and an upright bloom that
     * turns with it goes edge on to a fixed camera and disappears.
     */
    lamp.scale.setScalar(0.88 + 0.12 * Math.sin(t * 1.7))
    const parent = lamp.parent
    if (parent !== null) lamp.rotation.y = -parent.rotation.y
    burnLanterns(clock.darknessAt(clock.t))
  }
}

// ---------------------------------------------------------------------------
// the fishing float
// ---------------------------------------------------------------------------

/** How many boxes the line is made of. Odd, so one of them is the apex. */
const LINE_SEGS = 5
/** How high the line bows above the straight run. Clears the jetty. */
const LINE_LIFT = 1.15

/**
 * The float, its ripple ring, the line back to the rod, and the SHOUT that
 * says a fish is on.
 * Named parts: 'bob' (the thing that dips), 'ring' (the ripple), 'shout',
 * and 'line0'..'line4' (the bowed line).
 */
export function floatMesh(): THREE.Group {
  const g = group('float')
  /**
   * IT IS THE ONE THING A KID HAS TO WATCH, SO IT IS BIG — AND IT IS NOT A
   * LILY PAD.
   *
   * Round `vr1`: "the float is visually identical to the lily pads — same teal
   * oval with a white dot — so a kid cannot tell the float from the scenery."
   * The pads are flat, teal and lie ON the water. The float now answers every
   * one of those: it stands UP out of the water on a tall ink post with a gold
   * tip, and it is red and white, which is the one colour pair nothing else in
   * the pond wears.
   */
  const bob = group('bob')
  bob.add(halo(2.6))
  /**
   * BIGGER. Round `vr2`: "the float itself is a tiny red-and-white speck." At
   * the default zoom a 0.36 m ball is about eighteen screen pixels, sitting
   * among lily pads that are wider than it is. Every radius here is up about a
   * third, which costs nothing (the geometry cache keys on the radius, and there
   * is exactly one float in the world at a time).
   */
  const top = ball(0.48, { color: 'flowerRed', seg: 10 })
  top.position.y = 0.34
  top.castShadow = false
  const bottom = ball(0.48, { color: 'flowerWhite', seg: 10 })
  bottom.position.y = -0.02
  bottom.castShadow = false
  const stick = new THREE.Mesh(boxGeo(0.12, 0.9, 0.12), toon('ink', { flat: true }))
  stick.position.y = 1.0
  stick.castShadow = false
  const tip = ball(0.19, { color: 'uiCoin', seg: 8 })
  tip.position.y = 1.5
  tip.castShadow = false
  bob.add(bottom, top, stick, tip)
  g.add(bob)

  const ring = new THREE.Mesh(new THREE.RingGeometry(0.62, 0.9, 20), flat('flowerWhite', 0.72))
  ring.name = 'ring'
  ring.rotation.x = -Math.PI / 2
  ring.position.y = 0.02
  ring.castShadow = false
  ring.receiveShadow = false
  g.add(ring)

  /**
   * THE SHOUT, AND IT IS *ON* THE FLOAT.
   *
   * Round `vr2`: "the bite is a small yellow starburst drawn a long way from the
   * float and sitting on the jetty planks... the whole loop depends on the dip
   * being the most legible thing on screen, and right now it is a sparkle in the
   * wrong place."
   *
   * It was a 1.5 m solid star hung at y 2.05 — over two metres above the water,
   * which on this camera is most of a monster's height up and to the left of the
   * float, and lands wherever the scenery behind it happens to be. Two changes,
   * and they are a pair:
   *
   *  - it moves DOWN onto the ball (y 0.34, the red half's own height), so the
   *    burst and the thing a kid is watching are the same object; and
   *  - it becomes a RING of rays with a hole in the middle (`burstDraw`), 3.2 m
   *    across, so the float is visible THROUGH it rather than buried under it.
   *
   * It is a PICTURE, not a letterform (LAW #4): stepped hard bands, the same
   * graphic vocabulary as every other plate in this file. `renderOrder 2` with
   * `depthWrite: false` keeps it in front of the ball without punching a hole in
   * anything drawn after it.
   */
  const shout = new THREE.Mesh(quadGeo(3.8, 3.8), texturedFlat('catch:shout', burstDraw, {
    color: 'uiCoin',
  }))
  shout.name = 'shout'
  // Centred on the float, not above it. The lower rays go under the waterline
  // and are correctly occluded by the pond, so the burst erupts OUT of the water.
  shout.position.y = 0.7
  shout.visible = false
  shout.renderOrder = 2
  shout.castShadow = false
  g.add(shout)

  /**
   * THE LINE IS A BOW, NOT A HAIRLINE THROUGH THE JETTY.
   *
   * Round `vr1`: "a single white hairline from the monster's midriff to a float,
   * passing straight THROUGH the wooden jetty with visible z-fighting." Both
   * halves of that were true: 3 cm of pale cream is a hairline at this zoom, and
   * a straight segment between two points either side of the world crew's
   * walkway goes through the planks because that is where the straight line is.
   * It is now five ink-dark boxes following a bowed arc that clears the jetty
   * over the top, which is also simply what a cast line looks like.
   */
  for (let i = 0; i < LINE_SEGS; i++) {
    const seg = new THREE.Mesh(boxGeo(0.075, 1, 0.075), flat('ink', 0.9))
    seg.name = `line${i}`
    seg.castShadow = false
    seg.receiveShadow = false
    g.add(seg)
  }
  return g
}

/**
 * A stepped starburst with a HOLE IN THE MIDDLE — hard bands, no gradient, same
 * family as the plates.
 *
 * The hole is the whole point (see `floatMesh`): this is drawn centred on the
 * float rather than hung above it, so a solid star would simply hide the one
 * object a kid is supposed to be watching. `destination-out` punches the middle
 * back out after both stars are laid down, which keeps the two alpha bands
 * exact rather than approximating them with a third fill.
 */
function burstDraw(c: CanvasRenderingContext2D, s: number): void {
  const r = s / 2
  c.clearRect(0, 0, s, s)
  const star = (radius: number, inner: number, alpha: number): void => {
    c.beginPath()
    for (let i = 0; i < 16; i++) {
      const rad = i % 2 === 0 ? radius : inner
      const a = -Math.PI / 2 + (i * Math.PI) / 8
      const x = r + Math.cos(a) * rad
      const y = r + Math.sin(a) * rad
      if (i === 0) c.moveTo(x, y)
      else c.lineTo(x, y)
    }
    c.closePath()
    c.fillStyle = `rgba(255,255,255,${alpha})`
    c.fill()
  }
  star(r * 0.99, r * 0.62, 0.45)
  star(r * 0.86, r * 0.52, 1)
  c.globalCompositeOperation = 'destination-out'
  c.beginPath()
  c.arc(r, r, r * 0.33, 0, Math.PI * 2)
  c.fillStyle = '#fff'
  c.fill()
  c.globalCompositeOperation = 'source-over'
}

const lineA = new THREE.Vector3()
const lineB = new THREE.Vector3()
const lineMid = new THREE.Vector3()
const lineDir = new THREE.Vector3()
const lineUp = new THREE.Vector3(0, 1, 0)

function bowPoint(
  from: THREE.Vector3,
  to: THREE.Vector3,
  lift: number,
  k: number,
  out: THREE.Vector3,
): void {
  out.lerpVectors(from, to, k)
  out.y += Math.sin(k * Math.PI) * lift
}

/**
 * Bow the line from the rod tip down to the float. Pure arithmetic on cached
 * module-level vectors — this runs on every awake frame the rod is out, and the
 * older iPad has no allocation budget for a per-frame `clone()`.
 */
export function aimLine(g: THREE.Group, from: THREE.Vector3, to: THREE.Vector3): void {
  // `from` is the float end, `to` the rod tip. The bow lifts the middle.
  const lift = Math.min(LINE_LIFT, from.distanceTo(to) * 0.34)
  for (let i = 0; i < LINE_SEGS; i++) {
    const seg = g.getObjectByName(`line${i}`)
    if (seg === undefined) continue
    bowPoint(from, to, lift, i / LINE_SEGS, lineA)
    bowPoint(from, to, lift, (i + 1) / LINE_SEGS, lineB)
    lineDir.subVectors(lineB, lineA)
    const len = lineDir.length()
    lineMid.addVectors(lineA, lineB).multiplyScalar(0.5)
    g.worldToLocal(lineMid)
    seg.position.copy(lineMid)
    seg.scale.set(1, Math.max(0.01, len), 1)
    lineDir.normalize()
    seg.quaternion.setFromUnitVectors(lineUp, lineDir)
  }
}

/**
 * THE ROD IN THE MONSTER'S HANDS.
 *
 * Round `vr1`: "there is no rod in the monster's hands, and the monster is
 * standing side-on to the water in its normal idle pose." The pose belongs to
 * the monster crew and this file does not reach into it — but the ROD does not
 * have to. It is this crew's prop, held beside the monster and pointed at the
 * float, which is enough for the picture to say FISHING. Named part: 'tipAt'
 * is an empty at the rod tip, so the line has somewhere honest to start.
 */
/** Length of the shaft, in metres. The tip empty sits at exactly this height. */
const ROD_LEN = 1.45
/**
 * HOW FAR IN FRONT OF THE MONSTER THE HANDS ARE, and how far over it tips.
 *
 * Round `vr2`: "a bare black curve coming out of its face". Read the capture and
 * that is exactly what shipped — the rod WAS there, standing 0.42 m in front of
 * the monster's centre at y 0.62 and tipping only 0.62 rad, which on a fixed
 * isometric camera looking down at ~35° draws the shaft diagonally ACROSS the
 * head and puts the grip behind it. A rod you cannot see the bottom of is a
 * wire, and a wire leaving a face is a rendering bug to anybody looking at it.
 *
 * Two numbers move it off the body and nothing else has to change: the butt of
 * the rod now sits a whole body-radius forward (0.78) at hand height (0.5), and
 * the shaft leans 1.02 rad — a bit past 45° — so the whole rod is drawn between
 * the monster and the water instead of over it. Every part of it is in front of
 * the silhouette at every camera rotation, because "in front" here means
 * *toward the float*, which is what the yaw is derived from.
 */
const ROD_OUT = 0.7
const ROD_UP = 0.5
const ROD_TILT = 0.95

export function rodMesh(): THREE.Group {
  const g = group('rod')
  const arm = group('arm')
  /**
   * IT HAS TO READ AS A ROD AND NOT AS THE LINE. The shaft was `trunkDark` and
   * the line is `ink` at 0.9 — two dark strokes meeting end to end, which is why
   * the critic read the pair as one stray wire. The shaft is now pale `wood`
   * with a `trunkDark` butt and a gold reel, so where the rod stops and the line
   * starts is a colour change a three-year-old can see.
   */
  const shaft = new THREE.Mesh(boxGeo(0.11, ROD_LEN, 0.11), toon('wood', { flat: true }))
  shaft.position.y = ROD_LEN / 2
  shaft.castShadow = false
  arm.add(shaft)
  const grip = new THREE.Mesh(boxGeo(0.19, 0.46, 0.19), toon('trunkDark', { flat: true }))
  grip.position.y = 0.22
  grip.castShadow = false
  arm.add(grip)
  const reel = ball(0.17, { color: 'uiCoin', seg: 9 })
  reel.position.set(0.15, 0.56, 0)
  reel.castShadow = false
  arm.add(reel)
  // A whip at the top, so the tip is thinner than the butt and the thing has a
  // direction. One more box, no new material.
  const whip = new THREE.Mesh(boxGeo(0.07, 0.36, 0.07), toon('wood', { flat: true }))
  whip.position.y = ROD_LEN + 0.16
  whip.castShadow = false
  arm.add(whip)
  const tipAt = group('tipAt')
  tipAt.position.y = ROD_LEN + 0.34
  arm.add(tipAt)
  g.add(arm)
  return g
}

/**
 * Stand the rod in the monster's hands and point it at the water. Returns the
 * world position of its tip so the line has somewhere honest to start.
 */
export function aimRod(g: THREE.Group, at: THREE.Vector3, towards: THREE.Vector3, out: THREE.Vector3): void {
  const arm = g.getObjectByName('arm')
  if (arm === undefined) return
  const dx = towards.x - at.x
  const dz = towards.z - at.z
  const yaw = Math.atan2(dx, dz)
  g.position.set(at.x + Math.sin(yaw) * ROD_OUT, ROD_UP, at.z + Math.cos(yaw) * ROD_OUT)
  g.rotation.y = yaw
  // Tilted out over the water, the way anybody holds a rod.
  arm.rotation.x = ROD_TILT
  const tip = g.getObjectByName('tipAt')
  if (tip === undefined) out.copy(g.position)
  else tip.getWorldPosition(out)
}

// ---------------------------------------------------------------------------
// the catch
// ---------------------------------------------------------------------------

/** The fish that arcs out of the water. Big, bright, and gone in a second. */
export function catchMesh(id: string): THREE.Group {
  const look = lookOf(id)
  const g = group(`catch:${id}`)
  g.add(halo(2.2))
  const body = ball(0.34, { color: look.body, seg: 10 })
  body.scale.set(1.55, 0.85, 0.7)
  body.castShadow = false
  g.add(body)
  const tail = new THREE.Mesh(quadGeo(0.5, 0.44), flat(look.accent))
  tail.position.set(-0.55, 0, 0)
  tail.castShadow = false
  g.add(tail)
  const eye = ball(0.07, { color: 'eyeWhite', seg: 7 })
  eye.position.set(0.34, 0.09, 0.18)
  eye.castShadow = false
  g.add(eye)
  const pupil = ball(0.035, { color: 'pupil', seg: 6 })
  pupil.position.set(0.39, 0.09, 0.2)
  pupil.castShadow = false
  g.add(pupil)
  return g
}

/**
 * Give back the ONE piece of geometry this crew allocates per object: the
 * float's ripple ring. Everything else — every box, ball, quad and material —
 * comes from `src/render/kit.ts`'s cache and is shared with the rest of the
 * game, so disposing it would take the town's art with it.
 */
export function disposeCatchArt(g: THREE.Object3D): void {
  g.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.isMesh === true && mesh.geometry instanceof THREE.RingGeometry) mesh.geometry.dispose()
  })
}

/** Dev-only sanity read for the tools. */
export function creatureTint(id: string): string {
  return css(lookOf(id).body)
}

/** Kept so a tool can assert the plates are really textures and not flat quads. */
export function haloIsReady(): boolean {
  return platesReady()
}

export const INK_HEX = hex('ink')
